import { trackLongSession5Min } from "@/lib/analytics/gtm-datalayer";
import { getLanguageNameByCode } from "@/lib/livekit-agent-telemetry";
import {
  GUEST_USAGE_TODAY_KEY,
  getDateScopedUsageSeconds,
  getGuestTotalUsageSeconds,
  getRegisteredUsageSeconds,
  incrementGuestUsageSeconds,
  setRegisteredUsageSeconds,
} from "@/lib/livekit-agent-usage-storage";
import type { LiveKitSessionUsage } from "@/lib/livekit-session-usage";
import { DEFAULT_SESSION_MODE, type SessionMode } from "@/lib/sessionRouting";
import { useEffect, useRef } from "react";

type RecordingTimerLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type RecordingTimerUser = {
  email?: string | null;
};

type MutableRef<T> = {
  current: T;
};

type UseLiveKitRecordingTimerInput = {
  isRecording: boolean;
  user?: RecordingTimerUser | null;
  authenticatedSessionUsage: LiveKitSessionUsage | null;
  hasExceededGuestLimit: boolean;
  selectedLanguageCode: string;
  selectedLanguageLevel: string;
  sessionMode?: SessionMode;
  analyticsSurface: string;
  posthogClient?: PostHogLike | null;
  maxGuestSeconds: number;
  hasTrackedEngagedSessionRef: MutableRef<boolean>;
  hasTrackedGuestLimitReachedRef: MutableRef<boolean>;
  setRecordingTime: React.Dispatch<React.SetStateAction<number>>;
  setHasExceededGuestLimit: (value: boolean) => void;
  disconnectFromLiveKit: (
    triggeredByTimer?: boolean,
    invalidateTokenCache?: boolean,
    source?: string
  ) => void;
  logger: RecordingTimerLogger;
};

export function useLiveKitRecordingTimer({
  isRecording,
  user,
  authenticatedSessionUsage,
  hasExceededGuestLimit,
  selectedLanguageCode,
  selectedLanguageLevel,
  sessionMode,
  analyticsSurface,
  posthogClient,
  maxGuestSeconds,
  hasTrackedEngagedSessionRef,
  hasTrackedGuestLimitReachedRef,
  setRecordingTime,
  setHasExceededGuestLimit,
  disconnectFromLiveKit,
  logger,
}: UseLiveKitRecordingTimerInput) {
  const disconnectFromLiveKitRef = useRef(disconnectFromLiveKit);

  useEffect(() => {
    disconnectFromLiveKitRef.current = disconnectFromLiveKit;
  }, [disconnectFromLiveKit]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined = undefined;

    const trackAuthenticatedEngagedSessionIfNeeded = (durationSeconds: number) => {
      if (
        sessionMode === "maths" ||
        durationSeconds !== 300 ||
        hasTrackedEngagedSessionRef.current
      ) {
        return;
      }

      hasTrackedEngagedSessionRef.current = true;
      trackLongSession5Min({
        durationMinutes: 5,
        sessionMode: sessionMode || DEFAULT_SESSION_MODE,
        userType: "authenticated",
        selectedLanguageCode,
        selectedLanguageName: getLanguageNameByCode(selectedLanguageCode),
      });
      logger.info("Long session GA4 event tracked (5 min)");
    };

    if (isRecording && !user && !hasExceededGuestLimit) {
      interval = setInterval(() => {
        setRecordingTime((prevSessionTime) => prevSessionTime + 1);

        const newTotalUsage = incrementGuestUsageSeconds(logger);

        if (newTotalUsage >= maxGuestSeconds) {
          if (maxGuestSeconds >= 300 && !hasTrackedEngagedSessionRef.current) {
            hasTrackedEngagedSessionRef.current = true;
            trackLongSession5Min({
              durationMinutes: Number((maxGuestSeconds / 60).toFixed(2)),
              sessionMode: sessionMode || DEFAULT_SESSION_MODE,
              userType: "guest",
              selectedLanguageCode,
              selectedLanguageName: getLanguageNameByCode(selectedLanguageCode),
            });
            logger.info("Long session GA4 event tracked at guest demo completion", {
              guestSessionLimitSeconds: maxGuestSeconds,
            });
          }

          if (
            analyticsSurface === "fullscreen" &&
            posthogClient &&
            !hasTrackedGuestLimitReachedRef.current
          ) {
            hasTrackedGuestLimitReachedRef.current = true;
            posthogClient.capture("fullscreen_guest_demo_limit_reached", {
              selected_language_code: selectedLanguageCode,
              selected_language_name: getLanguageNameByCode(selectedLanguageCode),
              selected_language_level: selectedLanguageLevel,
              guest_seconds_used_total: newTotalUsage,
              guest_seconds_used_today: getDateScopedUsageSeconds(GUEST_USAGE_TODAY_KEY, logger),
            });
          }
          disconnectFromLiveKitRef.current(true, false, "guest_limit_timer");
          setHasExceededGuestLimit(true);
          if (interval) clearInterval(interval);
        }
      }, 1000);
    } else if (isRecording && user) {
      if (authenticatedSessionUsage?.policy === "freemium") {
        let meteredSessionSeconds = 0;
        interval = setInterval(() => {
          meteredSessionSeconds += 1;
          setRecordingTime((prevSessionTime) => {
            const newTime = prevSessionTime + 1;
            trackAuthenticatedEngagedSessionIfNeeded(newTime);
            return newTime;
          });
          if (user.email) {
            const used = getRegisteredUsageSeconds(user.email, logger);
            const updated = used + 1;
            setRegisteredUsageSeconds(user.email, updated);
          }
          if (meteredSessionSeconds >= authenticatedSessionUsage.sessionLimitSeconds) {
            if (sessionMode !== "maths") {
              disconnectFromLiveKitRef.current(true, false, "server_freemium_grant_timer");
            }
            if (interval) clearInterval(interval);
          }
        }, 1000);
      } else {
        interval = setInterval(() => {
          setRecordingTime((prevSessionTime) => {
            const newTime = prevSessionTime + 1;
            trackAuthenticatedEngagedSessionIfNeeded(newTime);
            return newTime;
          });
        }, 1000);
      }
    } else {
      setRecordingTime(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [
    analyticsSurface,
    hasExceededGuestLimit,
    hasTrackedEngagedSessionRef,
    hasTrackedGuestLimitReachedRef,
    isRecording,
    logger,
    maxGuestSeconds,
    posthogClient,
    selectedLanguageCode,
    selectedLanguageLevel,
    sessionMode,
    setHasExceededGuestLimit,
    setRecordingTime,
    authenticatedSessionUsage,
    user,
  ]);
}
